// Everything the client needs from its environment, read once.
//
// The two loops this client runs in differ in exactly one thing that
// matters here — whether a credential exists — so that is the flag, and
// nothing else branches on the environment.

/**
 * How the client obtains a credential.
 *
 * `none` is the local loop: the API runs with `AUTHORING_CLAIMS_REQUIRED`
 * unset, so membership is unbounded and no `Authorization` header is
 * sent. It is not "auth disabled in the client" — the client genuinely
 * has nothing to send, and a header would be a fiction.
 *
 * `cognito` is the deployed loop: the hosted UI, authorization code with
 * PKCE, and the **ID token** as the bearer. The access token carries
 * neither the subject nor the group claims the surface authorises on.
 */
export type AuthMode = 'none' | 'cognito'

interface RuntimeConfig {
  apiBaseUrl?: string
  previewOrigin?: string
  authMode?: AuthMode
  cognitoDomain?: string
  cognitoClientId?: string
  cognitoRegion?: string
  cognitoScopes?: string
}

declare global {
  interface Window {
    __BLUU_CMS_CONFIG__?: RuntimeConfig
  }
}

const runtimeConfig = window.__BLUU_CMS_CONFIG__

export interface CognitoConfig {
  /** The user pool's hosted-UI domain, e.g. `https://x.auth.ap-southeast-2.amazoncognito.com`. */
  domain: string
  /** The stage's app client id. Public, no secret — PKCE is what protects the exchange. */
  clientId: string
  /**
   * The pool's region, e.g. `ap-southeast-2`. The account area calls the
   * pool's own endpoint, `cognito-idp.<region>.amazonaws.com`, and the
   * hosted domain cannot be relied on to name it.
   */
  region: string
  /**
   * Scopes. `openid` is what mints an ID token at all; `email` and
   * `profile` are what put a human-readable identity on it.
   *
   * `aws.cognito.signin.user.admin` lets the access token act on the
   * signed-in account and no other — enrol a second factor, change the
   * password, sign out everywhere — which managed login does not offer a
   * pool whose MFA is optional (docs/plan/account.md). It authorises
   * nothing on the API: the ID token is still the credential there.
   */
  scopes: string
}

export interface Config {
  /**
   * The API's origin, including any stage path.
   *
* Deployed this is `https://api.cms.bluu.digital/<stage>`. The shared custom
* domain maps each application stage at its stage key, which preserves the
* `/<stage>` prefix expected by `API_BASE_PATH` and keeps dev and prod on one
* stable API hostname.
   */
  apiBaseUrl: string
  /**
   * Where a preview frame points, overriding the site's own address.
   *
   * **It exists so the frame is developed against the local loop.** A
   * preview that only works against a deployed stage is a preview nobody
   * develops against — and a seeded local tenant binds a hostname like
   * any other, so preferring the site's own canonical address would
   * point the frame at something no local machine resolves. This names
   * the local front-end instead, and the site's own path is appended to
   * it.
   *
   * A deployed stage leaves it **empty**, which is what makes the
   * override safe: there each site's canonical hostname is the address,
   * and one origin for every tenant would frame the wrong site.
   */
  previewOrigin: string
  authMode: AuthMode
  cognito: CognitoConfig | null
}

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is required when VITE_AUTH_MODE=cognito. ` +
        'A hosted-login build with no pool to log in to fails at the redirect, ' +
        'which is the worst place to discover it.',
    )
  }
  return value
}

function readAuthMode(): AuthMode {
  const mode = runtimeConfig?.authMode ?? import.meta.env.VITE_AUTH_MODE ?? 'none'
  if (mode !== 'none' && mode !== 'cognito') {
    throw new Error(`VITE_AUTH_MODE must be 'none' or 'cognito', not ${mode}`)
  }
  return mode
}

function readConfig(): Config {
  const authMode = readAuthMode()
  return {
    apiBaseUrl: (runtimeConfig?.apiBaseUrl ?? import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080').replace(
      /\/$/,
      '',
    ),
    previewOrigin: (runtimeConfig?.previewOrigin ?? import.meta.env.VITE_PREVIEW_ORIGIN ?? '').replace(/\/$/, ''),
    authMode,
    cognito:
      authMode === 'cognito'
        ? {
            domain: required(
              'VITE_COGNITO_DOMAIN',
              runtimeConfig?.cognitoDomain ?? import.meta.env.VITE_COGNITO_DOMAIN,
            ).replace(/\/$/, ''),
            clientId: required(
              'VITE_COGNITO_CLIENT_ID',
              runtimeConfig?.cognitoClientId ?? import.meta.env.VITE_COGNITO_CLIENT_ID,
            ),
            region: required('VITE_COGNITO_REGION', runtimeConfig?.cognitoRegion ?? import.meta.env.VITE_COGNITO_REGION),
            scopes:
              runtimeConfig?.cognitoScopes ??
              import.meta.env.VITE_COGNITO_SCOPES ??
              'openid email profile aws.cognito.signin.user.admin',
          }
        : null,
  }
}

export const config: Config = readConfig()

/**
 * Where Cognito sends an author back to. Derived from the running origin
 * rather than configured, because it must equal one of the callback URLs
 * the pool registered — and those are `AdminOrigins` in the CDK, which is
 * the same list that answers CORS. One value, two uses, and deriving it
 * here means a client served from a registered origin cannot disagree
 * with itself about where it lives.
 */
export const redirectUri = `${window.location.origin}/auth/callback`

/**
 * Where the pool sends an author after a sign-out.
 *
 * Derived for the same reason as `redirectUri`, and **the trailing slash
 * is load-bearing**: `LogoutURLs` in the CDK is each admin origin with
 * `/` appended, and Cognito compares a `logout_uri` for equality. Without
 * it the hosted logout answers `redirect_mismatch` — the session is
 * already gone locally by then, so it reads as "sign-out broke" rather
 * than as a registration that is one character off.
 */
export const logoutUri = `${window.location.origin}/`
